import * as AvatarPrimitive from "@radix-ui/react-avatar"
import clsx from "clsx"
import { cn } from "../../lib/utils"
import { Icon } from "../Icon/Icon"

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl"
export type AvatarVariant = "square" | "rounded"

const sizeClasses: Record<AvatarSize, string> = {
  xs: "w-8 h-8 text-10",
  sm: "w-10 h-10 text-12",
  md: "w-12 h-12 text-14",
  lg: "w-24 h-24 text-24",
  xl: "w-32 h-32 text-32",
}

const sizeIconClasses: Record<AvatarSize, string> = {
  xs: "w-4 h-4",
  sm: "w-4 h-4",
  md: "w-5 h-5",
  lg: "w-8 h-8",
  xl: "w-12 h-12",
}

const variantClasses: Record<AvatarVariant, string> = {
  square: "rounded-4",
  rounded: "rounded-[999px]",
}

interface AvatarProps extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {
  size?: AvatarSize
  variant?: AvatarVariant
}

const Avatar = ({ className, size = "sm", variant = "square", ...props }: AvatarProps) => (
  <AvatarPrimitive.Root
    className={cn(
      "relative flex shrink-0 overflow-hidden border-root-border-base border-1",
      sizeClasses[size],
      variantClasses[variant],
      className
    )}
    {...props}
  />
)
Avatar.displayName = "Avatar"

interface AvatarImageProps extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image> {}
const AvatarImage = ({ className, ...props }: AvatarImageProps) => (
  <AvatarPrimitive.Image
    className={cn("aspect-square w-full h-full object-cover", className)}
    {...props}
  />
)
AvatarImage.displayName = "AvatarImage"

interface AvatarFallbackProps
  extends Omit<React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>, "children"> {
  variant?: AvatarVariant
  size?: AvatarSize
  name?: string
}
const AvatarFallback = ({
  className,
  variant = "square",
  size = "sm",
  name,
  ...props
}: AvatarFallbackProps) => (
  <AvatarPrimitive.Fallback
    className={clsx(
      "flex items-center justify-center w-full h-full text-root-text-color-base",
      name?.length ? "bg-orange-400" : "bg-brand-secondary-muted",
      className
    )}
    {...props}
  >
    {name ? getInitials(name) : <Icon name="Person" className={sizeIconClasses[size]} />}
  </AvatarPrimitive.Fallback>
)
AvatarFallback.displayName = "AvatarFallback"

function getInitials(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .map((word) => word[0].toUpperCase())
    .join("")
}
export { Avatar, AvatarImage, AvatarFallback }
